import { Link } from 'react-router-dom';
import { RefreshCw, Menu } from 'lucide-react';
import { NotificationBell } from '../notifications/NotificationBell';
import { ConnectionIndicator } from '../common/ConnectionIndicator';
import { useAuthStore } from '../../store/authStore';
import { tenantLogo, tenantName } from '../../config/tenant';

interface Breadcrumb {
  label: string;
  path?: string;
}

interface TopBarProps {
  title: string;
  breadcrumbs?: Breadcrumb[];
  onRefresh?: () => void;
  onMenuToggle?: () => void;
}

export function TopBar({ title, breadcrumbs, onRefresh, onMenuToggle }: TopBarProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 flex-shrink-0">
      <div className="flex items-center gap-2 text-sm">
        {/* Hamburger — mobile only */}
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="md:hidden p-1.5 -ml-1 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Tenant logo — mobile only */}
        <img
          src={tenantLogo}
          alt={tenantName}
          className="md:hidden w-7 h-7 object-contain flex-shrink-0"
        />

        {breadcrumbs?.map((crumb, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span className="text-gray-400">/</span>}
            {crumb.path ? (
              <Link to={crumb.path} className="text-gray-500 hover:text-ems-green transition-colors">
                {crumb.label}
              </Link>
            ) : (
              <span className="text-gray-900 font-medium">{crumb.label}</span>
            )}
          </span>
        ))}
        {!breadcrumbs && <h1 className="text-gray-900 font-semibold truncate">{title}</h1>}
      </div>

      <div className="flex items-center gap-2">
        {isAuthenticated && <ConnectionIndicator />}
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        <NotificationBell />
      </div>
    </header>
  );
}
